/**
 * `<caos-difference-modal>` — two versions of one thing, side by side, with the comparison engine swappable.
 * (Catalog: Developer surfaces › DifferenceModal — one item's two versions side by side, with the comparison engine behind a seam.)
 *
 * The comparison engine is NOT imported. It is looked up through `diff-engine.js`, so the library
 * ships with a working line comparison and an application that wants a word-level or syntax-aware
 * one installs it without this component changing. Same clause of the specification the editor seam
 * honours, same reason.
 *
 * ONE SIDE WHEN IT EXISTS ON ONLY ONE SIDE. An item that is only in the source has nothing to be
 * compared against, and drawing an empty right-hand column beside it says "the target has this, and
 * it is blank" — which is a different and wronger fact. The missing side is replaced by a sentence
 * saying it is not there.
 *
 * AN IDENTICAL PAIR SAYS SO. Two versions that match produce no difference rows, and a modal that
 * drew them anyway with nothing highlighted leaves somebody hunting for a change that is not there.
 * It states that they are the same and shows the content once.
 *
 * THE COUNTS COME FROM THE ROWS. Added and removed are tallied from what the engine actually
 * produced rather than computed separately, so the line at the top cannot disagree with the lines
 * under it whatever engine is installed.
 *
 * Styling seams: `::part(difference)`, `::part(header)`, `::part(title)`, `::part(tally)`,
 * `::part(sides)`, `::part(side)`, `::part(side-name)`, `::part(rows)`, `::part(row)`,
 * `::part(gutter)`, `::part(text)`, `::part(absent)`, `::part(close)`.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';
import { diffEngine, diffTally, type DiffRow } from '../diff-engine.js';

const sheet = makeSheet(`
  :host { display: block; }

  .difference { display: flex; flex-direction: column; gap: var(--caos-space-3); }

  .header { display: flex; align-items: baseline; justify-content: space-between; gap: var(--caos-space-3); flex-wrap: wrap; }

  .title { font-family: var(--caos-font); font-size: 14px; font-weight: 600; color: var(--caos-color-text); margin: 0; }

  .tally { font-family: var(--caos-font); font-size: 12.5px; color: var(--caos-color-text-muted); margin: 0; }
  .tally .added { color: var(--caos-color-success-text); font-weight: 600; }
  .tally .removed { color: var(--caos-color-danger-text); font-weight: 600; }

  .close {
    border: 1px solid var(--caos-color-border);
    border-radius: var(--caos-radius-sm);
    background: var(--caos-color-surface);
    color: var(--caos-color-text);
    font-family: var(--caos-font);
    font-size: 12.5px;
    padding: 4px 10px;
    cursor: pointer;
  }
  .close:focus-visible { outline: none; box-shadow: var(--caos-focus-ring); }

  .sides { display: grid; grid-template-columns: 1fr 1fr; gap: var(--caos-space-2); }
  .sides[data-single="yes"] { grid-template-columns: 1fr; }

  .side-name {
    font-family: var(--caos-font);
    font-size: 11.5px;
    color: var(--caos-color-text-muted);
    padding-bottom: 2px;
  }

  .rows {
    border: 1px solid var(--caos-color-border);
    border-radius: var(--caos-radius-sm);
    overflow: auto;
    max-height: 420px;
    background: var(--caos-color-surface);
  }

  .row {
    display: grid;
    grid-template-columns: 44px minmax(0, 1fr);
    gap: var(--caos-space-2);
    font-family: var(--caos-font-mono);
    font-size: 11.5px;
    line-height: 1.6;
    padding: 0 8px;
  }
  .row[data-op="added"] { background: var(--caos-color-success-bg); }
  .row[data-op="removed"] { background: var(--caos-color-danger-bg); }
  /* A row on the other side has to keep its height so the two columns stay in step. */
  .row[data-op="spacer"] { background: var(--caos-color-surface-2); }

  .gutter { color: var(--caos-color-text-ghost); text-align: right; }
  .text { color: var(--caos-color-text); white-space: pre-wrap; word-break: break-word; }
  .row[data-op="spacer"] .text { color: transparent; }

  .absent {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 90px;
    padding: var(--caos-space-4);
    border: 1px dashed var(--caos-color-border);
    border-radius: var(--caos-radius-sm);
    font-family: var(--caos-font);
    font-size: 12.5px;
    color: var(--caos-color-text-muted);
    text-align: center;
  }

  .same { font-family: var(--caos-font); font-size: 12.5px; color: var(--caos-color-success-text); margin: 0; }
`);

/** Which side an item is missing from. Absent ⇒ it is on both. */
export type CaosMissingSide = 'source' | 'target';

export class CaosDifferenceModal extends CaosElement {
  static get observedAttributes(): string[] {
    return ['item', 'source-name', 'target-name', 'missing', 'closable'];
  }

  private titleEl: HTMLParagraphElement;
  private tallyEl: HTMLParagraphElement;
  private closeEl: HTMLButtonElement;
  private sidesEl: HTMLDivElement;

  private left = '';
  private right = '';

  constructor() {
    super(sheet);

    const wrap = document.createElement('div');
    wrap.className = 'difference';
    wrap.setAttribute('part', 'difference');

    const header = document.createElement('div');
    header.className = 'header';
    header.setAttribute('part', 'header');

    const stack = document.createElement('div');
    this.titleEl = document.createElement('p');
    this.titleEl.className = 'title';
    this.titleEl.setAttribute('part', 'title');
    this.tallyEl = document.createElement('p');
    this.tallyEl.className = 'tally';
    this.tallyEl.setAttribute('part', 'tally');
    this.tallyEl.setAttribute('role', 'status');
    stack.append(this.titleEl, this.tallyEl);

    this.closeEl = document.createElement('button');
    this.closeEl.type = 'button';
    this.closeEl.className = 'close';
    this.closeEl.setAttribute('part', 'close');
    this.closeEl.textContent = 'Close';
    this.closeEl.addEventListener('click', () =>
      this.dispatchEvent(new CustomEvent('caos-difference-close', { bubbles: true, composed: true })),
    );

    header.append(stack, this.closeEl);

    this.sidesEl = document.createElement('div');
    this.sidesEl.className = 'sides';
    this.sidesEl.setAttribute('part', 'sides');

    wrap.append(header, this.sidesEl);
    this.root.append(wrap);
  }

  connectedCallback(): void {
    this.render();
  }

  attributeChangedCallback(): void {
    this.render();
  }

  /** The source side's text. */
  get sourceText(): string {
    return this.left;
  }

  set sourceText(text: string) {
    this.left = typeof text === 'string' ? text : '';
    this.render();
  }

  /** The target side's text. */
  get targetText(): string {
    return this.right;
  }

  set targetText(text: string) {
    this.right = typeof text === 'string' ? text : '';
    this.render();
  }

  /** Which side the item is missing from, when it is on only one. */
  get missing(): CaosMissingSide | null {
    const asked = this.getAttribute('missing');
    return asked === 'source' || asked === 'target' ? asked : null;
  }

  /** The comparison, as the installed engine produced it. Empty for a one-sided item. */
  get rows(): readonly DiffRow[] {
    if (this.missing !== null) return [];
    return diffEngine().diff(this.left, this.right);
  }

  /** How many rows of each kind — computed FROM the rows, so the line cannot disagree with them. */
  get tally(): { added: number; removed: number; equal: number } {
    return diffTally(this.rows);
  }

  /** Whether the two sides are identical. A one-sided item is not "identical", it is one-sided. */
  get identical(): boolean {
    if (this.missing !== null) return false;
    const t = this.tally;
    return t.added === 0 && t.removed === 0;
  }

  /** Which engine produced the rows — the seam, readable, so a surface can say what it installed. */
  get engine(): string {
    return diffEngine().name;
  }

  private render(): void {
    const item = this.getAttribute('item') ?? '';
    const sourceName = this.getAttribute('source-name') ?? 'Source';
    const targetName = this.getAttribute('target-name') ?? 'Target';

    this.titleEl.textContent = item;
    this.closeEl.hidden = !this.hasAttribute('closable');

    if (this.missing !== null) {
      const present = this.missing === 'source' ? targetName : sourceName;
      this.tallyEl.textContent = `Only in ${present}`;
      this.sidesEl.setAttribute('data-single', 'yes');
      // ONE column. An empty second column beside it says "the other side has this, and it is
      // blank", which is a different and wronger fact.
      this.sidesEl.replaceChildren(
        this.side(present, this.missing === 'source' ? this.right : this.left, 'present'),
        this.absent(this.missing === 'source' ? sourceName : targetName, item),
      );
      return;
    }

    this.sidesEl.removeAttribute('data-single');

    if (this.identical) {
      const same = document.createElement('p');
      same.className = 'same';
      // Said, rather than drawn as two columns with nothing highlighted — which leaves somebody
      // hunting for a change that is not there.
      same.textContent = 'The two versions are identical.';
      this.tallyEl.replaceChildren(same);
      this.sidesEl.setAttribute('data-single', 'yes');
      this.sidesEl.replaceChildren(this.side(`${sourceName} and ${targetName}`, this.left, 'present'));
      return;
    }

    const t = this.tally;
    const removed = document.createElement('span');
    removed.className = 'removed';
    removed.textContent = `${t.removed} removed`;
    const added = document.createElement('span');
    added.className = 'added';
    added.textContent = `${t.added} added`;
    this.tallyEl.replaceChildren(removed, document.createTextNode(' · '), added);

    this.sidesEl.replaceChildren(this.sideFromRows(sourceName, 'left'), this.sideFromRows(targetName, 'right'));
  }

  private side(name: string, text: string, _kind: 'present'): HTMLElement {
    const box = document.createElement('div');
    box.setAttribute('part', 'side');
    const label = document.createElement('p');
    label.className = 'side-name';
    label.setAttribute('part', 'side-name');
    label.textContent = name;
    const rows = document.createElement('div');
    rows.className = 'rows';
    rows.setAttribute('part', 'rows');
    text.split('\n').forEach((line, i) => rows.append(this.row('equal', i + 1, line)));
    box.append(label, rows);
    return box;
  }

  private absent(name: string, item: string): HTMLElement {
    const box = document.createElement('div');
    box.setAttribute('part', 'side');
    const label = document.createElement('p');
    label.className = 'side-name';
    label.setAttribute('part', 'side-name');
    label.textContent = name;
    const note = document.createElement('p');
    note.className = 'absent';
    note.setAttribute('part', 'absent');
    note.textContent = item === '' ? `Not in ${name}.` : `${item} is not in ${name}.`;
    box.append(label, note);
    return box;
  }

  private sideFromRows(name: string, which: 'left' | 'right'): HTMLElement {
    const box = document.createElement('div');
    box.setAttribute('part', 'side');
    const label = document.createElement('p');
    label.className = 'side-name';
    label.setAttribute('part', 'side-name');
    label.textContent = name;

    const rows = document.createElement('div');
    rows.className = 'rows';
    rows.setAttribute('part', 'rows');

    for (const row of this.rows) {
      const mine = which === 'left' ? row.left : row.right;
      const number = which === 'left' ? row.leftLine : row.rightLine;
      if (mine === undefined) {
        // A spacer, so the two columns stay in step. Without it the sides drift apart by one line
        // per change, and after four changes nothing lines up with anything.
        rows.append(this.row('spacer', undefined, ''));
        continue;
      }
      rows.append(this.row(row.op, number, mine));
    }

    box.append(label, rows);
    return box;
  }

  private row(op: string, number: number | undefined, text: string): HTMLElement {
    const row = document.createElement('div');
    row.className = 'row';
    row.setAttribute('part', 'row');
    row.setAttribute('data-op', op);
    if (op === 'spacer') row.setAttribute('aria-hidden', 'true');

    const gutter = document.createElement('span');
    gutter.className = 'gutter';
    gutter.setAttribute('part', 'gutter');
    gutter.textContent = number === undefined ? '' : String(number);

    const body = document.createElement('span');
    body.className = 'text';
    body.setAttribute('part', 'text');
    body.textContent = text;

    // The change is named, not only coloured — a row read aloud says what happened to it.
    if (op === 'added' || op === 'removed') row.setAttribute('aria-label', `${op}: ${text}`);

    row.append(gutter, body);
    return row;
  }
}

define('caos-difference-modal', CaosDifferenceModal);

declare global {
  interface HTMLElementTagNameMap {
    'caos-difference-modal': CaosDifferenceModal;
  }
}
